<script setup lang="ts">
import { computed, reactive, ref, onMounted, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import KpFooter from '@/components/KpFooter.vue';
import { globalVar, useGlobals } from '@/helpers/globals';
import { useAuthUser, isAuthenticated } from '@/helpers/globals';
import { appUrl, apiUrl } from '@/helpers/api';
import { logout } from '@/helpers/auth';
import { RouterLink, useRouter } from 'vue-router';
import { login } from '@/helpers/auth';
import LoginDialog from '@/components/LoginDialog.vue';
import ChangeRoleDialog from '@/components/ChangeRoleDialog.vue';
import AccountNavigationAuth from '@/components/AccountNavigationAuth.vue';
import AccountNavigationUnauth from '@/components/AccountNavigationUnauth.vue';
import NavBreadcrumbs from '@/components/NavBreadcrumbs.vue';
import { useBrowserSession } from '@kp/geofy-vuejs-lib/tools/browser_session.js';
import { on as onGlobalEvent } from './composables/useGlobalEventBus';
import '@kanton-thurgau/design-system/modal';
import { KTGModalElement } from '@kanton-thurgau/design-system/modal';
import KpModal from '@kp/geofy-vuejs-lib/tg-system/components/KpModal.vue';
import EnvironmentBanner from '@/components/EnvironmentBanner.vue';
import AgovDialog from '@/components/AgovDialog.vue';

const globals = useGlobals();
const route = useRoute();
const router = useRouter();
const browserSession = useBrowserSession('eplan');

const model = reactive({
    loading: false,
    error: null as string | null,
    showLoginDialog: false as boolean,
    showRoleDialog: false as boolean
});

const showLogoutMsg: Ref<boolean> = ref(false);
const showAgovDialog: Ref<boolean> = ref(false);
const agovRedirectUrl: Ref<string> = ref(appUrl('/'));
const authUser = useAuthUser();
const username = computed(() => authUser?.name ?? '-');
const loginFail = computed(() => {
    return route.query.loginFail;
});
const logoutUrl: Ref<string | null> = ref(null);
let afterLoginRedirectUrl: string | null = null;

const items = globals.show_under_development_info
    ? ref([])
    : authUser?.active_role === 'admin'
      ? ref([
            {
                label: 'Planungsgeschäfte',
                itemId: 'planungsgeschaefte',
                route: { name: 'planungsgeschaefte-overview', params: {} }
            },
            {
                label: `Administration`,
                itemId: 'administration',
                route: { name: 'admin-users', params: {} }
            },
            {
                label: `Hilfe & Kontakt`,
                itemId: 'help',
                route: { name: 'help-faq', params: {} }
            }
        ])
      : ref([
            {
                label: 'Planungsgeschäfte',
                itemId: 'planungsgeschaefte',
                route: { name: 'planungsgeschaefte-overview', params: {} }
            },
            {
                label: `Hilfe & Kontakt`,
                itemId: 'help',
                route: { name: 'help-faq', params: {} }
            }
        ]);

// Register global login event handling:
onGlobalEvent('login', (redirectUrl: string) => {
    const loginMethod = globalVar('login_method');
    if (loginMethod === 'oidc') {
        showAgovLogin(redirectUrl);
    } else {
        afterLoginRedirectUrl = redirectUrl;
        onLoginBtn();
    }
});

onMounted(async() => {
    await router.isReady();

    const logout = route.query.logout;
    if (logout !== undefined) {
        showLogoutMsg.value = true;
        logoutUrl.value = apiUrl('/auth/logout') + '?oidc=1';
    }

    if (route.query.loginFail) {
        gemeindeLogout();
    }

    watch(loginFail, gemeindeLogout);
});

watch(showLogoutMsg, (newVal, oldVal) => {
   if (oldVal && !newVal) {
    window.location.href = appUrl('');
   }
});

function gemeindeLogout() {
    if (route.query.loginFail) {
        console.log('no gemeinde');
        const modal = document.getElementById('dangermouse')! as KTGModalElement;
        logoutUrl.value = apiUrl('/auth/logout') + '?oidc=1';
        modal.open();
    }
}

function showOldSchoolLogin() {
    const env = globals.environment;
    return !!(env === 'dev' || (env === 'test' && route.query.enableBasicAuth));
}

function onLoginBtn() {
    model.showLoginDialog = true;
}

function onChangeRoleBtn() {
    model.showRoleDialog = true;
}


async function onRoleChange(role: string) {
    if (!authUser) {
        return;
    }
    const oldVal = authUser.active_role;
    try {
        if (role.split('::').length === 2) {
            authUser.active_role = role.split('::')[0];
            authUser.active_role_stelle_id = parseInt(role.split('::')[1], 10);
        } else {
            authUser.active_role = role;
            authUser.active_role_stelle_id = null;
        }

        await authUser.save();
        model.showRoleDialog = false;
        browserSession.clearAllSessionStorage();
        document.location.href = appUrl('/');
    } catch (e) {
        console.error(e);
        authUser.active_role = oldVal;
        //this.$toast({ message: errToStr(e), color: 'error' });
    }
}

async function onLogin({ username, password }: { username: string; password: string }) {
    model.loading = true;
    model.error = null;
    try {
        await login(username, password);
        document.location.href = afterLoginRedirectUrl || appUrl('/');
    } catch (e) {
        console.error(e);
        model.error = 'Username oder Passwort falsch';
    } finally {
        model.loading = false;
    }
}

// OIDC login always goes through the AGOV info dialog first:
function showAgovLogin(redirectUrl: string = appUrl('/')) {
    agovRedirectUrl.value = redirectUrl;
    showAgovDialog.value = true;
}

function onLogoutBtn() {
    onLogout();
}

async function onLogout() {
    model.loading = true;
    model.error = null;
    try {
        await logout();
        //document.location.href = appUrl('/', { logout: true });
    } catch (e) {
        console.error(e);
        model.error = 'Etwas ist schief gelaufen!';
    } finally {
        model.loading = false;
    }
}

function closeDangerMouse() {
    const modal = document.getElementById('dangermouse')! as KTGModalElement;
    modal.close();
}

</script>
<template>
    <EnvironmentBanner />
    <ktg-horizon></ktg-horizon>
    <div class="sticky-header">
        <!-- eslint-disable vue/no-deprecated-slot-attribute -->
        <ktg-main-navigation :home-url="globals.frontend_base" logo-description="Zur Startseite">
            <!-- Meta navigation, top light gray area -->
            <ktg-horizontal-navigation label="Meta-Navigation" slot="meta">
                <ktg-meta-navigation-item href="https://www.tg.ch"> Startseite tg.ch </ktg-meta-navigation-item>
            </ktg-horizontal-navigation>

            <!-- Primary navigation -->
            <ktg-horizontal-navigation
                v-if="!globals.show_under_development_info"
                label="Hauptnavigation"
                slot="primary"
            >
                <template v-for="item in items" :key="item.itemId">
                    <RouterLink :to="item.route" v-slot="{ isActive }">
                        <ktg-horizontal-navigation-item :selected="isActive">
                            {{ item.label }}
                        </ktg-horizontal-navigation-item>
                    </RouterLink>
                </template>
            </ktg-horizontal-navigation>

            <AccountNavigationAuth
                v-if="!globals.show_under_development_info && isAuthenticated()"
                :username="username"
                @logout="onLogoutBtn"
                @changerole="onChangeRoleBtn"
            />
            <AccountNavigationUnauth
                v-if="!globals.show_under_development_info && !isAuthenticated()"
                :show-local-login="showOldSchoolLogin()"
                @login="onLoginBtn"
                @oidcLogin="showAgovLogin()"
            />
            <ktg-hamburger></ktg-hamburger>
        </ktg-main-navigation>

        <!-- eslint-enable vue/no-deprecated-slot-attribute -->
    </div>

    <nav class="ktg-main-container mt-1_5rem mb-4_5rem">
        <NavBreadcrumbs />
    </nav>

    <RouterView></RouterView>

    <KpFooter class="footer" />

    <LoginDialog
        v-model="model.showLoginDialog"
        :loading="model.loading"
        modal
        :header="$t('login')"
        :style="{ width: '25rem' }"
        :error="model.error"
        @login="onLogin"
        @cancel="model.showLoginDialog = false"
    />

    <ChangeRoleDialog
        v-model="model.showRoleDialog"
        :loading="model.loading"
        modal
        :style="{ width: '25rem' }"
        @rolechange="onRoleChange"
        @cancel="model.showRoleDialog = false"
    />

    <ktg-modal id="dangermouse" appearance="danger">
        <h2 class="ktg-modal-title"> {{ $t('fehlende_berechtigung') }} </h2>

        <div class="ktg-modal-text" v-html="$t('no_gemeinde_login_msg')" />

        <ktg-link trailing-icon="long-arrow-right" :href="appUrl('help/faq')" class="mb-0_5rem">{{ $t('more_infos_access') }} </ktg-link>

        <ktg-modal-buttons vertical>
            <ktg-button @click="closeDangerMouse" class="cancel-button" appearance="primary" size="small" full-width> {{ $t('confirm') }} </ktg-button>
        </ktg-modal-buttons>
    </ktg-modal>

    <KpModal v-model="showLogoutMsg">{{ $t('err_msg_forced_logout') }}</KpModal>
    <AgovDialog v-model="showAgovDialog" :redirect-url="agovRedirectUrl" />
    <iframe v-if="logoutUrl" :src="logoutUrl" width="0" height="0" style="display: none" />
</template>

<style scoped lang="scss">
.sticky-header {
    position: sticky;
    top: 0;
    z-index: var(--ktg-layer-main-navigation);
}
</style>
